<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { watch } from 'vue'
import * as yup from 'yup'
import {
  type UpdateServiceProviderPayload,
  updateServiceProviderAction,
} from '@/api/functions/services/providers/update-service-provider.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

interface ProviderFormValues {
  name: string
  slug: string
  website_url: string
  description: string
  is_active: boolean
  logo: File | null
}

const props = defineProps<{
  modelValue: boolean
  selectedProvider: Record<string, any>
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
  (event: 'confirm'): void
  (event: 'cancel'): void
}>()

const toast = useToast()

const schema = yup.object({
  name: yup
    .string()
    .required('نام ارائه‌دهنده الزامی است')
    .min(2, 'نام ارائه‌دهنده باید حداقل ۲ کاراکتر باشد')
    .max(100, 'نام ارائه‌دهنده نمی‌تواند بیشتر از ۱۰۰ کاراکتر باشد'),

  slug: yup
    .string()
    .trim()
    .required('اسلاگ ارائه‌دهنده الزامی است')
    .min(2, 'اسلاگ باید حداقل ۲ کاراکتر باشد')
    .max(100, 'اسلاگ نمی‌تواند بیشتر از ۱۰۰ کاراکتر باشد')
    .matches(/^[a-z0-9]+(?:_[a-z0-9]+)*$/, 'اسلاگ فقط می‌تواند شامل حروف انگلیسی کوچک، عدد و آندرلاین باشد'),

  website_url: yup.string().required('آدرس وب‌سایت الزامی است').url('آدرس وب‌سایت معتبر نیست'),

  description: yup
    .string()
    .required('توضیحات الزامی است')
    .min(10, 'توضیحات باید حداقل ۱۰ کاراکتر باشد')
    .max(1000, 'توضیحات نمی‌تواند بیشتر از ۱۰۰۰ کاراکتر باشد'),

  is_active: yup.boolean().required(),

  logo: yup.mixed<File>().nullable(),
})

const { handleSubmit, errors, resetForm } = useForm<ProviderFormValues>({
  validationSchema: schema,
  initialValues: {
    name: '',
    slug: '',
    website_url: '',
    description: '',
    is_active: true,
    logo: null,
  },
})

const { value: name } = useField<string>('name')
const { value: slug } = useField<string>('slug')
const { value: website_url } = useField<string>('website_url')
const { value: description } = useField<string>('description')
const { value: is_active } = useField<boolean>('is_active')
const { value: logo } = useField<File | null>('logo')

const statusOptions = [
  {
    label: 'فعال',
    value: true,
  },
  {
    label: 'غیرفعال',
    value: false,
  },
]

watch(
  () => props.selectedProvider,
  (provider) => {
    if (!provider?.id) return

    resetForm({
      values: {
        name: provider.name ?? '',
        slug: provider.slug ?? '',
        website_url: provider.website_url ?? '',
        description: provider.description ?? '',
        is_active: provider.is_active ?? provider.status === 'ACTIVE',
        logo: null,
      },
    })
  },
  {
    immediate: true,
    deep: true,
  }
)

const updateProviderMutation = useMutationApi({
  mutationFn: (payload: UpdateServiceProviderPayload) =>
    updateServiceProviderAction(props.selectedProvider.id, payload),

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const isSubmitting = () => updateProviderMutation.isPending.value

const onSubmit = handleSubmit(async (values) => {
  const payload: UpdateServiceProviderPayload = {
    name: values.name.trim(),
    slug: values.slug.trim(),
    description: values.description.trim(),
    website_url: values.website_url.trim(),
    is_active: values.is_active,
  }

  try {
    await updateProviderMutation.mutateAsync(payload)

    toast.success('ارائه‌دهنده با موفقیت ویرایش شد')

    emit('confirm')
    closeModal()
  } catch {}
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>


<template>
    <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
        background="#FFF" :closable="true" title="ویرایش ارائه‌دهنده" closeDirection="left" class="create-provider">

        <form @submit.prevent="onSubmit" class="flex flex-col min-h-[600px]">

            <div class="flex-1 overflow-y-auto px-4 py-6 pb-9">

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

                    <Input v-model="name" name="name" label="نام ارائه‌دهنده" type="text" :error="errors.name" />


                    <Input v-model="slug" name="slug" label="اسلاگ" type="text" :error="errors.slug" />


                    <Input v-model="website_url" name="website_url" label="آدرس وب‌سایت" type="url"
                        :error="errors.website_url" />


                    <Select v-model="is_active" name="is_active" label="وضعیت" :options="statusOptions"
                        :error="errors.is_active" />


                    <div class="lg:col-span-2">

                        <Textarea v-model="description" name="description" label="توضیحات"
                            :error="errors.description" />

                    </div>


                    <div class="lg:col-span-2 mt-20">

                        <FileUploader v-model="logo" label="لوگوی ارائه‌دهنده" :error="errors.logo" />

                    </div>

                </div>

            </div>


            <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">

                <Button type="submit" label="ویرایش ارائه‌دهنده" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
                    :disabled="isSubmitting()" :loading="isSubmitting()" />


                <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
                    class="w-4/12 h-12 rounded-xl border-2" :disabled="isSubmitting()" />

            </div>

        </form>

    </Modal>
</template>